import * as React from 'react'
import { CaretSortIcon, CheckIcon } from '@radix-ui/react-icons'
import { Popover, PopoverContent, PopoverTrigger } from './popover'
import { Button } from './button'
import { cn } from '@/common/lib/utils'
import { Input } from './input'
import { useQueryState, parseAsString } from 'nuqs'

interface ComboboxProps {
	options: {
		label: string
		value: string
	}[]
	value?: string
	onValueChange: (value: string) => void
	placeholder?: string
	className?: string
}

export function Combobox({
	options,
	value,
	onValueChange,
	placeholder,
	className,
}: ComboboxProps) {
	const [open, setOpen] = React.useState(false)
	const [searchQuery, setSearchQuery] = useQueryState(
		'comboboxSearch',
		parseAsString.withOptions({ shallow: false }).withDefault(''),
	)

	const filteredOptions = React.useMemo(() => {
		if (!searchQuery) return options
		return options.filter((option) =>
			option.label.toLowerCase().includes(searchQuery.toLowerCase()),
		)
	}, [options, searchQuery])

	const selectedOption = React.useMemo(
		() => options.find((option) => option.value === value),
		[options, value],
	)

	return (
		<Popover open={open} onOpenChange={setOpen}>
			<PopoverTrigger asChild>
				<Button
					variant="outline"
					role="combobox"
					aria-expanded={open}
					className={cn('w-[180px] justify-between', className)}
				>
					{open && searchQuery
						? searchQuery
						: selectedOption?.label || placeholder || 'Select...'}
					<CaretSortIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
				</Button>
			</PopoverTrigger>
			<PopoverContent className="w-[180px] p-0" align="start">
				<div className="border-b p-2">
					<Input
						placeholder="ค้นหา..."
						className="h-8 border-0 focus-visible:ring-0"
						value={searchQuery ?? ''}
						onChange={(e) => setSearchQuery(e.target.value || null)}
					/>
				</div>
				<div className="max-h-[200px] overflow-y-auto">
					{filteredOptions.length > 0 ? (
						<React.Fragment key={filteredOptions.length}>
							{filteredOptions.map((option) => (
								<div
									key={option.value}
									className={cn(
										'relative flex cursor-default select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none hover:bg-accent hover:text-accent-foreground',
										option.value === value &&
											'bg-accent text-accent-foreground',
									)}
									onClick={() => {
										onValueChange(option.value)
										setOpen(false)
										setSearchQuery(null)
									}}
								>
									{option.label}
									{option.value === value && (
										<CheckIcon className="ml-auto h-4 w-4" />
									)}
								</div>
							))}
						</React.Fragment>
					) : (
						<div className="py-6 text-center text-sm text-muted-foreground">
							No results found.
						</div>
					)}
				</div>
			</PopoverContent>
		</Popover>
	)
}
